import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import type { TransitionPhase, TransitionState } from 'react-transitioning'
import { layering } from '@mobi/component-library/Foundations'
import { lessOrEqualThan } from '@mobi/component-library/Utils/media'
import { hexColors } from '@mobi/settings'
import { BET_SLIP_BUTTON_ID } from '@core/Areas/AppHeader/Components/MyBetsButtons/Components/BetSlipButton/constants'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'
import type { ModalItem } from './types'

const enum LocalConstants {
  BezierUp = 'cubic-bezier(0.165, 0.84, 0.44, 1)',
  BezierDown = 'cubic-bezier(0.550, 0.085, 0.680, 0.530)',
}

export const ANIMATION_DURATION_MS = 400

export const ModalScreenCoverStyled = styled.div({
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  top: 0,
  bottom: 0,
  width: '100%',
  zIndex: layering.modalOverlay,
  // Prevent iPhone zooming when tapping buttons quickly
  touchAction: 'manipulation',
})

export const ModalPositionStyled = styled.div<{ position: ModalItem['position'] }>(
  {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '48rem',
    minHeight: '10rem',
    margin: '0 auto',
    backgroundColor: 'transparent',
    willChange: 'auto',
  },
  ({ position = 'bottom' }: { position: ModalItem['position'] }) => ({
    justifyContent: { bottom: 'flex-end', middle: 'center' }[position],
  })
)

export const ModalAnimationContainerStyled = styled.div<{
  transitionState: TransitionState
  activePhase: TransitionPhase
  exitAnimation?: ModalItem['exitAnimation']
}>(
  {
    position: 'relative',
    zIndex: 2,
    padding: '0 1.5rem 1.5rem',
    paddingBottom: shouldApplySafeAreaBottomPadding
      ? 'calc(1.5rem + var(--safe-area-inset-bottom, 0px))'
      : undefined,

    [lessOrEqualThan('px350')]: {
      padding: 0,
      paddingBottom: shouldApplySafeAreaBottomPadding
        ? 'var(--safe-area-inset-bottom, 0px)'
        : undefined,
    },

    overflowY: 'auto',
    overscrollBehavior: 'contain',
    animation: `moveUp ${ANIMATION_DURATION_MS}ms ${LocalConstants.BezierUp} forwards`,

    '@keyframes moveUp': { from: { transform: 'translateY(100%)' } },
  },
  ({
    transitionState,
    activePhase,
    exitAnimation,
  }: {
    transitionState: TransitionState
    activePhase: TransitionPhase
    exitAnimation?: ModalItem['exitAnimation']
  }) => {
    if (activePhase === 'exit' && transitionState.exitActive) {
      return {
        animation:
          exitAnimation && Object.prototype.hasOwnProperty.call(customAnimations, exitAnimation)
            ? customAnimations[exitAnimation]()
            : `${moveDown} ${ANIMATION_DURATION_MS}ms ${LocalConstants.BezierDown} forwards`,
      }
    }

    return undefined
  }
)

export const ModalContentContainerStyled = styled.div<{ cornerStyle: ModalItem['cornerStyle'] }>(
  {
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: hexColors.white,
  },
  ({ cornerStyle = 'square' }: { cornerStyle: ModalItem['cornerStyle'] }) =>
    cornerStyle === 'round' && {
      maskImage: 'radial-gradient(white, black)',
      borderRadius: '1rem',
    }
)

export const ModalCloseButtonStyled = styled('button')<Pick<ModalItem, 'color'>>(
  {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '5.5rem',
    height: '4rem',
    padding: 0,
    border: 0,
    backgroundColor: 'transparent',
    fontSize: '1.6rem',
  },
  ({ color = 'light' }: Pick<ModalItem, 'color'>) => ({
    color: color === 'light' ? hexColors.black : hexColors.white,
  })
)

export const ModalBackgroundStyled = styled.div<{
  transitionState: TransitionState
  activePhase: TransitionPhase
}>(
  {
    position: 'absolute',
    zIndex: 1,
    top: 0,
    bottom: 0,
    width: '100%',
    opacity: 0,
    transition: `opacity ${ANIMATION_DURATION_MS}ms linear`,
    background: 'rgba(0, 0, 0, 0.7)',
  },
  ({
    transitionState,
    activePhase,
  }: {
    transitionState: TransitionState
    activePhase: TransitionPhase
  }) => {
    if (activePhase === 'exit' && transitionState.exitActive) {
      return {
        animation: `${fadeOut} ${ANIMATION_DURATION_MS}ms ${LocalConstants.BezierDown} forwards`,
      }
    } else {
      return {
        animation: `${fadeIn} ${ANIMATION_DURATION_MS}ms ${LocalConstants.BezierUp} forwards`,
      }
    }
  }
)

const moveDown = keyframes({ to: { transform: 'translateY(100%)' } })
const fadeIn = keyframes({ to: { opacity: 1 } })
const fadeOut = keyframes({ from: { opacity: 1 } })

// =================
// Animation Helpers
// =================

const customAnimations = {
  addToBetslip: () =>
    modalAnimation(
      document.getElementById(QUICKBET_MODAL_ID),
      document.querySelector('#betslipButton') || document.getElementById(BET_SLIP_BUTTON_ID)
    ),
}

type ModalAnimationElement = HTMLElement | null
const modalAnimation = (source: ModalAnimationElement, target: ModalAnimationElement) => {
  if (!source || !target) return `${fadeOut} ${ANIMATION_DURATION_MS + 50}ms linear forwards`

  const {
    left: sourceLeft,
    top: sourceTop,
    height: sourceHeight,
    width: sourceWidth,
  } = source.getBoundingClientRect()
  const {
    left: targetLeft,
    top: targetTop,
    height: targetHeight,
    width: targetWidth,
  } = target.getBoundingClientRect()

  const scale = targetHeight / sourceHeight
  const translateX = Math.floor(targetLeft - sourceLeft - sourceWidth / 2 + targetWidth / 2)
  const translateY = Math.floor(targetTop - sourceTop - sourceHeight / 2 + targetHeight)

  const translateToTarget = keyframes({
    to: {
      transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
      opacity: 0.5,
    },
  })

  return `${translateToTarget} ${ANIMATION_DURATION_MS + 50}ms linear forwards`
}
